import React, { useState, useCallback } from 'react';
import { Plus, Trash2, Pencil, Check, X, CheckCircle2, Circle, Boxes } from 'lucide-react';
import { useLLDPractice, Entity } from '../../context/LLDPracticeContext';
import { validateEntity } from '../../utils/lldValidation';

interface ExpectedEntity {
  name: string;
  description: string;
  attributes: object[];
  methods: object[];
}

interface EntityDiscoveryPanelProps {
  expectedEntities?: ExpectedEntity[];
  onNavigateToResponsibilities?: () => void;
}

const EntityDiscoveryPanel: React.FC<EntityDiscoveryPanelProps> = ({
  expectedEntities = [],
  onNavigateToResponsibilities,
}) => {
  const { state, dispatch } = useLLDPractice();
  const { entities } = state.workspaceState;
  const { mode } = state.sessionState;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Inline edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editError, setEditError] = useState<string | null>(null);

  const isLearnMode = mode === 'learn';

  const handleAddEntity = useCallback(() => {
    const existingNames = entities.map((e) => e.name || '').filter(Boolean);
    const result = validateEntity(name.trim(), description, existingNames);

    if (!result.valid) {
      setError(result.error || 'Validation failed');
      return;
    }

    const newEntity: Entity = {
      id: crypto.randomUUID(),
      name: name.trim(),
      description: description.trim(),
      createdAt: Date.now(),
    };

    dispatch({ type: 'ADD_ENTITY', payload: newEntity });
    setName('');
    setDescription('');
    setError(null);
  }, [name, description, entities, dispatch]);

  const handleRemoveEntity = useCallback(
    (id: string) => {
      dispatch({ type: 'REMOVE_ENTITY', payload: id });
    },
    [dispatch]
  );

  const handleStartEdit = useCallback((entity: Entity) => {
    setEditingId(entity.id);
    setEditName(entity.name || '');
    setEditDescription(entity.description || '');
    setEditError(null);
  }, []);

  const handleSaveEdit = useCallback(() => {
    if (!editingId) return;

    const existingNames = entities
      .filter((e) => e.id !== editingId)
      .map((e) => e.name || '')
      .filter(Boolean);
    const result = validateEntity(editName.trim(), editDescription, existingNames);

    if (!result.valid) {
      setEditError(result.error || 'Validation failed');
      return;
    }

    dispatch({
      type: 'UPDATE_ENTITY',
      payload: {
        id: editingId,
        updates: { name: editName.trim(), description: editDescription.trim() },
      },
    });

    setEditingId(null);
    setEditName('');
    setEditDescription('');
    setEditError(null);
  }, [editingId, editName, editDescription, entities, dispatch]);

  const handleCancelEdit = useCallback(() => {
    setEditingId(null);
    setEditName('');
    setEditDescription('');
    setEditError(null);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && name.trim()) {
      handleAddEntity();
    }
  };

  // Check which expected entities the user has already discovered (case-insensitive)
  const getDiscoveredExpected = useCallback(() => {
    const userNames = new Set(
      entities
        .map((e) => (typeof e.name === 'string' ? e.name.trim().toLowerCase() : ''))
        .filter(Boolean)
    );
    return expectedEntities.map((expected) => ({
      ...expected,
      discovered: userNames.has(expected.name.toLowerCase()),
    }));
  }, [entities, expectedEntities]);

  return (
    <div className="flex flex-col h-full gap-4">
      {/* Add Entity Form */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (error) setError(null);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Entity name (e.g. ParkingLot)"
            maxLength={50}
            className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400"
          />
          <button
            onClick={handleAddEntity}
            disabled={!name.trim() || entities.length >= 50}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 transition-colors flex-shrink-0"
          >
            <Plus className="w-4 h-4" />
            Add Entity
          </button>
        </div>
        <input
          type="text"
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={handleKeyDown}
          placeholder="Short description (optional)"
          maxLength={200}
          className="mt-2 w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400"
        />
        {error && <p className="mt-2 text-sm text-rose-500 dark:text-rose-400">{error}</p>}
      </div>

      {/* Entity List */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Your entities <span className="text-gray-500 dark:text-gray-500 font-normal">({entities.length})</span>
        </h3>
      </div>

      <div className="flex-1 overflow-y-auto -mx-1 px-1">
        {entities.length === 0 ? (
          <div className="h-full min-h-[160px] flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 dark:border-gray-700 text-center px-6">
            <Boxes className="w-8 h-8 text-gray-300 dark:text-gray-600 mb-2" />
            <p className="text-sm text-gray-600 dark:text-gray-400">No entities yet.</p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">Start with the nouns in the problem statement.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            {entities.map((entity) =>
              editingId === entity.id ? (
                // Inline Edit Mode
                <div
                  key={entity.id}
                  className="p-3 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl space-y-2"
                >
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => {
                      setEditName(e.target.value);
                      if (editError) setEditError(null);
                    }}
                    maxLength={50}
                    className="w-full px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                    autoFocus
                  />
                  <input
                    type="text"
                    value={editDescription}
                    onChange={(e) => {
                      setEditDescription(e.target.value);
                      if (editError) setEditError(null);
                    }}
                    maxLength={200}
                    placeholder="Description (optional)"
                    className="w-full px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                  />
                  {editError && <p className="text-sm text-rose-500 dark:text-rose-400">{editError}</p>}
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveEdit}
                      className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm flex items-center gap-1 transition-colors"
                    >
                      <Check className="w-3.5 h-3.5" /> Save
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm flex items-center gap-1 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                // Display Mode
                <div
                  key={entity.id}
                  className="group relative flex items-start gap-3 p-3.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl hover:border-primary-300 dark:hover:border-primary-700 hover:shadow-sm transition-all"
                >
                  <div className="mt-0.5 w-8 h-8 rounded-lg bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-300 flex items-center justify-center flex-shrink-0 font-mono text-sm font-semibold">
                    {(typeof entity.name === 'string' && entity.name.trim()
                      ? entity.name.trim().charAt(0).toUpperCase()
                      : '?')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                      {typeof entity.name === 'string' && entity.name.trim() ? entity.name : 'Untitled Entity'}
                    </p>
                    {entity.description && (
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-2">{entity.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleStartEdit(entity)}
                      className="p-1.5 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                      title="Edit entity"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleRemoveEntity(entity.id)}
                      className="p-1.5 text-gray-400 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg transition-colors"
                      title="Remove entity"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>

      {/* Learn Mode: Expected Entities Checklist */}
      {isLearnMode && expectedEntities.length > 0 && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40 p-4">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-500 mb-2.5">
            Expected entities
          </h4>
          <div className="flex flex-wrap gap-2">
            {getDiscoveredExpected().map((expected) => (
              <span
                key={expected.name}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${expected.discovered
                  ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700'
                  }`}
              >
                {expected.discovered ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                {expected.name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EntityDiscoveryPanel;
